import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import SearchIcon from '@mui/icons-material/Search'
import { CircularProgress, Checkbox } from '@mui/joy'
import axios from 'axios'
import { formatToUserLocalTime } from '@/utils/date-formatting'
import { SelectedTrucksMapModal } from './SelectedTrucksMapModal'

export interface TruckLocationHistoryRow {
    projectName: string
    vin: string
    name: string
    lat: number
    lng: number
    fuelLevel: number | null
    speed: number | null
    headingDegree: number | null
    assignedLoad: string
    timestamp: string
}

const PAGE_SIZE = 100

export default function TruckHistory() {
    const [rows, setRows] = React.useState<TruckLocationHistoryRow[]>([])
    const [page, setPage] = React.useState(1)
    const [loading, setLoading] = React.useState(true)
    const [fetching, setFetching] = React.useState(false)
    const [totalCount, setTotalCount] = React.useState(0)
    const [totalPages, setTotalPages] = React.useState(0)

    const [projectFilter, setProjectFilter] = React.useState('')
    const [vinFilter, setVinFilter] = React.useState('')
    const [nameFilter, setNameFilter] = React.useState('')
    const [assignedLoadFilter, setAssignedLoadFilter] = React.useState('')
    const [timestampFromFilter, setTimestampFromFilter] = React.useState('')
    const [timestampToFilter, setTimestampToFilter] = React.useState('')
    const [appliedProjectFilter, setAppliedProjectFilter] = React.useState('')
    const [appliedVinFilter, setAppliedVinFilter] = React.useState('')
    const [appliedNameFilter, setAppliedNameFilter] = React.useState('')
    const [appliedAssignedLoadFilter, setAppliedAssignedLoadFilter] = React.useState('')
    const [appliedTimestampFromFilter, setAppliedTimestampFromFilter] = React.useState('')
    const [appliedTimestampToFilter, setAppliedTimestampToFilter] = React.useState('')

    const [selectedTrucks, setSelectedTrucks] = React.useState<Set<string>>(new Set())
    const [showMapModal, setShowMapModal] = React.useState(false)

    const allSelected = rows.length > 0 && rows.every((row, index) => selectedTrucks.has(`${row.vin}-${index}`))
    const isIndeterminate = !allSelected && rows.some((row, index) => selectedTrucks.has(`${row.vin}-${index}`))

    const applyFilters = () => {
        setPage(1)
        setAppliedProjectFilter(projectFilter)
        setAppliedVinFilter(vinFilter)
        setAppliedNameFilter(nameFilter)
        setAppliedAssignedLoadFilter(assignedLoadFilter)
        setAppliedTimestampFromFilter(timestampFromFilter)
        setAppliedTimestampToFilter(timestampToFilter)
    }

    const handleFilterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            applyFilters()
        }
    }

    const handleCheckboxChange = (truckKey: string, checked: boolean) => {
        setSelectedTrucks((prev) => {
            const newSet = new Set(prev)
            if (checked) {
                newSet.add(truckKey)
            } else {
                newSet.delete(truckKey)
            }
            return newSet
        })
    }

    const handleShowTrucks = () => {
        setShowMapModal(true)
    }

    const selectedTruckLocations = React.useMemo(() => rows.filter((row, index) => selectedTrucks.has(`${row.vin}-${index}`)), [rows, selectedTrucks])

    const singleProjectName = React.useMemo(() => {
        const names = new Set<string>()
        selectedTruckLocations.forEach((row) => {
            const value = (row.projectName || '').trim()
            if (value) {
                names.add(value)
            }
        })
        return names.size === 1 ? Array.from(names)[0] : null
    }, [selectedTruckLocations])

    const singleAssignedLoadId = React.useMemo(() => {
        const ids = new Set<string>()
        selectedTruckLocations.forEach((row) => {
            const value = (row.assignedLoad || '').trim()
            if (value) {
                ids.add(value)
            }
        })
        return ids.size === 1 ? Array.from(ids)[0] : null
    }, [selectedTruckLocations])

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const isInitialLoad =
                    page === 1 &&
                    !appliedProjectFilter &&
                    !appliedVinFilter &&
                    !appliedNameFilter &&
                    !appliedAssignedLoadFilter &&
                    !appliedTimestampFromFilter &&
                    !appliedTimestampToFilter

                if (isInitialLoad) {
                    setLoading(true)
                } else {
                    setFetching(true)
                }

                const params = new URLSearchParams()
                params.append('page', page.toString())
                params.append('pageSize', PAGE_SIZE.toString())
                if (appliedProjectFilter) params.append('projectName', appliedProjectFilter)
                if (appliedVinFilter) params.append('vin', appliedVinFilter)
                if (appliedNameFilter) params.append('name', appliedNameFilter)
                if (appliedAssignedLoadFilter) params.append('assignedLoad', appliedAssignedLoadFilter)
                if (appliedTimestampFromFilter) params.append('timestampFrom', appliedTimestampFromFilter)
                if (appliedTimestampToFilter) params.append('timestampTo', appliedTimestampToFilter)

                const res = await axios.get(`gluon/truck-history?${params.toString()}`)
                const data = res.data || {}
                setRows(Array.isArray(data.rows) ? data.rows : [])
                setTotalCount(typeof data.total === 'number' ? data.total : 0)
                setTotalPages(typeof data.totalPages === 'number' ? data.totalPages : 0)
            } catch (e) {
                setRows([])
                setTotalCount(0)
                setTotalPages(0)
            } finally {
                setLoading(false)
                setFetching(false)
            }
        }

        fetchData()
    }, [page, appliedProjectFilter, appliedVinFilter, appliedNameFilter, appliedAssignedLoadFilter, appliedTimestampFromFilter, appliedTimestampToFilter])

    // Clamp page when totalPages shrinks
    React.useEffect(() => {
        if (page > totalPages) {
            setPage(1)
        }
    }, [totalPages, page])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        )
    }

    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    my: 2,
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'start', sm: 'center' },
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography level="h2" component="h1">
                        Truck History ({totalCount})
                    </Typography>
                    {fetching && <CircularProgress size="sm" />}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" disabled={page === 1 || fetching} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page === totalPages || fetching} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Project Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by project name..."
                            startDecorator={<SearchIcon />}
                            value={projectFilter}
                            onChange={(e) => setProjectFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>VIN</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by VIN..."
                            startDecorator={<SearchIcon />}
                            value={vinFilter}
                            onChange={(e) => setVinFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by name..."
                            startDecorator={<SearchIcon />}
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="sm">
                        <FormLabel>Assigned Load</FormLabel>
                        <Input
                            size="sm"
                            placeholder="Search by assigned load..."
                            startDecorator={<SearchIcon />}
                            value={assignedLoadFilter}
                            onChange={(e) => setAssignedLoadFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                        />
                    </FormControl>
                    <FormControl size="sm" sx={{ minWidth: 160 }}>
                        <FormLabel>From</FormLabel>
                        <Input
                            size="sm"
                            type="date"
                            value={timestampFromFilter}
                            onChange={(e) => setTimestampFromFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                            slotProps={{ input: { max: timestampToFilter || undefined } }}
                        />
                    </FormControl>
                    <FormControl size="sm" sx={{ minWidth: 160 }}>
                        <FormLabel>To</FormLabel>
                        <Input
                            size="sm"
                            type="date"
                            value={timestampToFilter}
                            onChange={(e) => setTimestampToFilter(e.target.value)}
                            onKeyDown={handleFilterKeyDown}
                            slotProps={{ input: { min: timestampFromFilter || undefined } }}
                        />
                    </FormControl>
                    <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        onClick={applyFilters}
                        disabled={fetching}
                        startDecorator={<SearchIcon />}
                        sx={{ alignSelf: 'flex-end' }}
                    >
                        Search
                    </Button>
                    {selectedTrucks.size > 0 && (
                        <Button size="sm" variant="outlined" color="primary" onClick={handleShowTrucks} sx={{ alignSelf: 'flex-end' }}>
                            Show Trucks ({selectedTrucks.size})
                        </Button>
                    )}
                </Box>
            </Box>

            <Sheet
                variant="outlined"
                sx={{
                    display: { xs: 'none', sm: 'initial' },
                    width: '100%',
                    borderRadius: 'sm',
                    flexShrink: 1,
                    overflow: 'auto',
                    minHeight: 0,
                }}
            >
                <Table
                    aria-labelledby="truck-history-table-title"
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                        '--TableCell-paddingY': '4px',
                        '--TableCell-paddingX': '8px',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 40, padding: '12px 6px' }}>
                                <Checkbox
                                    size="sm"
                                    checked={allSelected}
                                    indeterminate={isIndeterminate}
                                    onChange={(e) => {
                                        const checked = e.target.checked
                                        if (checked) {
                                            setSelectedTrucks((prev) => {
                                                const newSet = new Set(prev)
                                                rows.forEach((row, index) => {
                                                    newSet.add(`${row.vin}-${index}`)
                                                })
                                                return newSet
                                            })
                                        } else {
                                            setSelectedTrucks((prev) => {
                                                const newSet = new Set(prev)
                                                rows.forEach((row, index) => {
                                                    newSet.delete(`${row.vin}-${index}`)
                                                })
                                                return newSet
                                            })
                                        }
                                    }}
                                />
                            </th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Project Name</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>VIN</th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Name</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Lat</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Lng</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Speed</th>
                            <th style={{ width: 120, padding: '12px 6px' }}>Fuel Level</th>
                            <th style={{ width: 200, padding: '12px 6px' }}>Assigned Load</th>
                            <th style={{ width: 160, padding: '12px 6px' }}>Created at</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => {
                            const truckKey = `${row.vin}-${index}`
                            return (
                                <tr key={`${row.projectName}-${row.vin}-${index}`}>
                                    <td>
                                        <Checkbox
                                            size="sm"
                                            checked={selectedTrucks.has(truckKey)}
                                            onChange={(e) => handleCheckboxChange(truckKey, e.target.checked)}
                                        />
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.projectName}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.vin}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.name}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.lat}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.lng}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.speed ?? 'N/A'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.fuelLevel ?? 'N/A'}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.assignedLoad}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">{row.timestamp ? formatToUserLocalTime(row.timestamp) : 'N/A'}</Typography>
                                    </td>
                                </tr>
                            )
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={10} style={{ textAlign: 'center', padding: '16px' }}>
                                    <Typography level="body-sm">No data available</Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>

            {showMapModal && (
                <SelectedTrucksMapModal
                    open={showMapModal}
                    onClose={() => setShowMapModal(false)}
                    trucks={selectedTruckLocations}
                    singleProjectName={singleProjectName}
                    singleAssignedLoadId={singleAssignedLoadId}
                />
            )}
        </React.Fragment>
    )
}
